import { useCallback } from 'react'
import UAParser from 'ua-parser-js'
import shallow from 'zustand/shallow'

import { datadogRum } from '@datadog/browser-rum'
import { DirectTask } from '@frame/lib/api/tasks/types'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'

import { PartnerEventPayload } from '../api/events/types'
import { EventSource } from '../api/events/types'
import { useRootContext } from '../context'
import { useStore } from '../stores/useStore'

export type PartnerEventPayloadDynamic = {
  event_name: string
  event_source: EventSource
  [key: string]: unknown
}
type PartnerEventPayloadStatic = {
  sessionToken: string
  version: string
  instanceId: string
  publishableKey: string
  task?: DirectTask
  referrer?: string
}

function getScreenSize() {
  const {
    screen: { width, height },
  } = window

  return `${width}x${height}`
}

function getViewportWidth() {
  const { visualViewport } = window

  if (!visualViewport) return ''

  return visualViewport.width
}

function getViewportHeight() {
  const { visualViewport } = window

  if (!visualViewport) return ''

  return visualViewport.height
}

function parseUserAgent(): UAParser.IResult {
  const parser = new UAParser(navigator.userAgent)
  return parser.getResult()
}

function getBrowserInfo() {
  const { os, browser } = parseUserAgent()
  const osName = os.name || ''
  const browserName = browser.name || ''
  const screenSize = getScreenSize()
  const viewportSize = getViewportSize()
  const viewportWidth = getViewportWidth()
  const viewportHeight = getViewportHeight()

  return {
    browser: browserName,
    os: osName,
    screen_size: screenSize,
    viewport_size: viewportSize,
    viewport_width: viewportWidth,
    viewport_height: viewportHeight,
  }
}

function getViewportSize() {
  const { visualViewport } = window

  if (!visualViewport) return ''

  return `${visualViewport.width}x${visualViewport.height}`
}

export const createPartnerEventPayloadCallback = ({
  sessionToken,
  version,
  instanceId,
  publishableKey,
  task,
  referrer,
}: PartnerEventPayloadStatic) => {
  return (payload: PartnerEventPayloadDynamic): PartnerEventPayload => {
    return {
      truework_js_session_token: sessionToken,
      created_at: new Date(),
      truework_js_event_version: '1.0',
      truework_js_version: version,
      instance_id: instanceId,
      publishable_key: publishableKey,
      browser_info: getBrowserInfo(),
      employment_id: task?.id,
      referrer,
      ...payload,
    }
  }
}

export const useMonitoring = () => {
  const { actions } = useRootContext()
  const task = useDirectTask()
  const { sessionToken, publishableKey, version, instanceId, referrer } = useStore(
    (state) => ({
      sessionToken: state.config.sessionToken,
      publishableKey: state.config.publishableKey,
      version: state.version,
      instanceId: state.instanceId,
      referrer: state.referrer,
    }),
    shallow
  )

  return useCallback(
    (payload: PartnerEventPayloadDynamic, callback?: () => void) => {
      const fullPayload = createPartnerEventPayloadCallback({
        sessionToken,
        version,
        instanceId,
        publishableKey,
        task,
        referrer,
      })(payload)

      datadogRum.addAction(fullPayload.event_name)
      actions.monitorPartnerEvent(fullPayload).finally(() => {
        if (callback) {
          callback()
        }
      })
    },
    [actions, instanceId, publishableKey, sessionToken, version, task, referrer]
  )
}
